/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
function patchSelectLabels() {
  document.querySelectorAll('select.acp-link-navigator-select').forEach((select) => {
    // Skip if already patched
    if (select.hasAttribute('aria-label')) return;

    const placeholder = select.querySelector('option[disabled]');
    if (!placeholder) return;

    const key = (placeholder.getAttribute('data-ap-translate') || placeholder.textContent).trim();

    if (key.includes('LINK')) {
      select.setAttribute('aria-label', 'Page link navigator');
    } else if (key.includes('COLORBLIND')) {
      select.setAttribute('aria-label', 'Select colorblind mode');
    } else {
      // Fallback for any other selects the widget adds
      select.setAttribute('aria-label', 'Accessibility option');
    }
  });
}

// Run immediately in case elements already exist
patchSelectLabels();

// Watch for widget injecting elements dynamically
const observer = new MutationObserver(() => {
  patchSelectLabels();
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Stop observing after 10s to avoid running forever
setTimeout(() => observer.disconnect(), 10000);


// ============================================
// Fix duplicate image alt text
// Numbers ALL product images - Image 1, 2, 3 etc
// ============================================

function patchDuplicateAltText() {
  // Find each product card wrapper
  const productCards = document.querySelectorAll('.product-block, .product-card, [class*="product-item"]');

  if (!productCards.length) {
    // Fallback: if no card wrapper found, just number all product-block__image globally
    const allImgs = document.querySelectorAll('.product-block__image img');
    allImgs.forEach((img, i) => {
      if (img.dataset.altPatched) return;
      const baseAlt = img.alt.replace(/ - Image \d+$/, '').trim() || 'Product image';
      img.alt = `${baseAlt} - Image ${i + 1}`;
      img.dataset.altPatched = 'true';
    });
    return;
  }

  productCards.forEach((card) => {
    const imgs = card.querySelectorAll('.product-block__image img');
    imgs.forEach((img, i) => {
      if (img.dataset.altPatched) return;
      // Strip any previously added " - Image X" before re-numbering
      const baseAlt = img.alt.replace(/ - Image \d+$/, '').trim() || 'Product image';
      img.alt = `${baseAlt} - Image ${i + 1}`;
      img.dataset.altPatched = 'true';
    });
  });
}

patchDuplicateAltText();

const altObserver = new MutationObserver(() => {
  patchDuplicateAltText();
});

altObserver.observe(document.body, {
  childList: true,
  subtree: true
});

setTimeout(() => altObserver.disconnect(), 15000);

// ============================================
// Fix duplicate alt text in product page slider
// Targets .slider__item images
// ============================================

function patchSliderAltText() {
  const sliders = document.querySelectorAll('.slider__grid');

  sliders.forEach((slider) => {
    const slides = slider.querySelectorAll('.slider__item');

    slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (!img || img.dataset.sliderAltPatched) return;

      const baseAlt = img.alt.replace(/ - Image \d+$/, '').trim() || 'Product image';
      img.alt = `${baseAlt} - Image ${i + 1}`;
      img.dataset.sliderAltPatched = 'true';
    });
  });
}

patchSliderAltText();

const sliderObserver = new MutationObserver(() => {
  patchSliderAltText();
});

sliderObserver.observe(document.body, {
  childList: true,
  subtree: true
});

setTimeout(() => sliderObserver.disconnect(), 15000);

// ============================================
// Fix: Remove nested focusable element inside button
// Moves .asw-hide-widget-icon outside #asw-icon
// ============================================

function fixNestedFocusable() {
  const menuBtn = document.querySelector('#asw-icon');
  const hideBtn = menuBtn ? menuBtn.querySelector('.asw-hide-widget-icon') : null;

  if (!menuBtn || !hideBtn || menuBtn.dataset.nestedFixed) return;

  // Move hide button to sit after the main button in the DOM
  menuBtn.parentNode.insertBefore(hideBtn, menuBtn.nextSibling);

  // Ensure it retains its accessibility attributes
  hideBtn.setAttribute('aria-label', 'Hide accessibility widget');
  hideBtn.setAttribute('role', 'button');
  hideBtn.setAttribute('tabindex', '0');

  // Position it on top of where it visually was (bottom-left of the widget button)
  hideBtn.style.position = 'absolute';
  hideBtn.style.zIndex = '99999';

  // Sync its position to sit on top-right corner of the main button
  const rect = menuBtn.getBoundingClientRect();
  hideBtn.style.top = (rect.top + window.scrollY) + 'px';
  hideBtn.style.left = (rect.left + rect.width - 20 + window.scrollX) + 'px';

  menuBtn.dataset.nestedFixed = 'true';
}

fixNestedFocusable();

// Re-run on scroll/resize to keep position in sync
window.addEventListener('resize', fixNestedFocusable);
window.addEventListener('scroll', fixNestedFocusable);

const nestedObserver = new MutationObserver(() => {
  fixNestedFocusable();
});

nestedObserver.observe(document.body, {
  childList: true,
  subtree: true
});

setTimeout(() => nestedObserver.disconnect(), 15000);

// Sync aria-expanded on filter toggle button
document.addEventListener('click', function (e) {
  const toggle = e.target.closest('[data-toggle-filters]');
  if (!toggle) return;

  const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isExpanded));
});

// Fix Judge.me nested interactive control inside role="img"
function patchJudgeMeRatingSummary() {
  document.querySelectorAll('.jdgm-rev-widg__summary-stars').forEach((container) => {
    if (container.dataset.patched) return;

    // Remove role="img" — it cannot contain focusable children
    container.removeAttribute('role');

    // The aria-label on the container is now redundant without role="img"
    // Move it to a visually hidden span instead so it's still announced
    const existingLabel = container.getAttribute('aria-label');
    if (existingLabel) {
      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = existingLabel;
      container.prepend(hiddenLabel);
      container.removeAttribute('aria-label');
    }

    // Hide decorative star spans from screen readers
    container.querySelectorAll('.jdgm-star').forEach((star) => {
      star.setAttribute('aria-hidden', 'true');
    });

    // Give the link a descriptive aria-label
    const link = container.querySelector('a.jdgm-link');
    if (link) {
      const rating = link.textContent.trim();
      link.setAttribute('aria-label', `Rated ${rating} — view reviews on Judge.me (opens in new tab)`);
    }

    container.dataset.patched = 'true';
  });
}

patchJudgeMeRatingSummary();

const judgeMeSummaryObserver = new MutationObserver(() => {
  patchJudgeMeRatingSummary();
});

judgeMeSummaryObserver.observe(document.body, {
  childList: true,
  subtree: true
});

setTimeout(() => judgeMeSummaryObserver.disconnect(), 10000);

// Sync aria-expanded on mobile nav toggle
function patchMobileNavToggle() {
  const toggle = document.querySelector('.mobile-nav-toggle');
  const nav = document.getElementById('main-nav');

  if (!toggle || !nav) return;
  if (toggle.dataset.patched) return;

  toggle.addEventListener('click', function () {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isExpanded));
  });

  // Also watch if the theme opens/closes nav programmatically (e.g. outside clicks)
  const navObserver = new MutationObserver(() => {
    const isOpen = 
      nav.classList.contains('is-open') || 
      nav.classList.contains('is-visible') ||
      nav.getAttribute('aria-hidden') === 'false';

    toggle.setAttribute('aria-expanded', String(isOpen));
  });

  navObserver.observe(nav, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });

  toggle.dataset.patched = 'true';
}

patchMobileNavToggle();

// Fallback in case nav isn't in DOM yet
const navToggleObserver = new MutationObserver(() => {
  patchMobileNavToggle();
});

navToggleObserver.observe(document.body, { childList: true, subtree: true });
setTimeout(() => navToggleObserver.disconnect(), 10000);

function patchSearchInputLabel() {
  const searchInput = document.getElementById('s4com-q');
  if (searchInput && !searchInput.hasAttribute('aria-label')) {
    searchInput.setAttribute('aria-label', 'Search products');
  }
}

patchSearchInputLabel();
setTimeout(patchSearchInputLabel, 500);
setTimeout(patchSearchInputLabel, 1500);

const searchObserver = new MutationObserver(() => patchSearchInputLabel());
searchObserver.observe(document.body, { childList: true, subtree: true });
setTimeout(() => searchObserver.disconnect(), 10000);

function patchFaqAccordionAria() {
  document.querySelectorAll('a[data-hc-toggle-article-listener-is-set="yes"]').forEach((toggle) => {

    const titleEl = toggle.querySelector('.s4com-article-title');
    const label = titleEl ? titleEl.textContent.trim() : 'FAQ item';

    const href = toggle.getAttribute('href') || '';
    const targetId = href.replace('#', '');
    const panel = document.getElementById(targetId);

    if (!toggle.hasAttribute('role')) {
      toggle.setAttribute('role', 'button');
    }

    if (panel) {
      toggle.setAttribute('aria-controls', targetId);
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-label', label);

      // Use computed style instead of inline style — catches all display changes
      const isExpanded = window.getComputedStyle(panel).display !== 'none';
      toggle.setAttribute('aria-expanded', String(isExpanded));
      toggle.setAttribute('aria-label', `${label}, ${isExpanded ? 'expanded' : 'collapsed'}`);
    }
  });
}

patchFaqAccordionAria();
setInterval(patchFaqAccordionAria, 300);
setTimeout(() => clearInterval(window._faqPollId), 300000);